import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { user, APIdomain } = useAuth();
  const navigate = useNavigate();

  // Fetch all courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${APIdomain}/api/courses`);
        const data = await response.json();

        if (data.success) {
          setCourses(data.data);
        } else {
          setError("Failed to fetch courses");
        }
      } catch (err) {
        setError("Error loading courses");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [APIdomain]);

  const fetchCourseDetails = async (courseId) => {
    try {
      setLoading(true);
      const response = await fetch(`${APIdomain}/api/courses/${courseId}`);
      const data = await response.json();

      if (data.success) {
        setSelectedCourse(data.data);
      } else {
        setError("Failed to fetch course details");
      }
    } catch (err) {
      const course = courses.find((c) => c._id === courseId);
      setSelectedCourse(course || null);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseClick = (courseId) => {
    fetchCourseDetails(courseId);
  };

  const handleBackToHome = () => {
    setSelectedCourse(null);
  };

  const isCoursePurchased = (courseId) => {
    return user?.purchases?.includes(courseId) || false;
  };

  // NEEDS IMPLEMENTATION
  const handlePurchaseCourse = (courseId) => {
    // Implement purchase logic here
    alert(`Purchase course ${courseId} - Implementation needed`);
  };

  // NEEDS IMPLEMENTATION
  const handleGoToCourse = (courseId) => {
    // Implement navigation to course content
    alert(`Navigate to course content ${courseId} - Implementation needed`);
  };

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f9fafb",
      padding: "2rem 1rem",
    },
    header: {
      textAlign: "center",
      marginBottom: "3rem",
    },
    title: {
      fontSize: "2.5rem",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "0.5rem",
    },
    subtitle: {
      color: "#6b7280",
      fontSize: "1.125rem",
    },
    coursesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
      gap: "1.5rem",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    courseCard: {
      backgroundColor: "white",
      padding: "1.5rem",
      borderRadius: "0.5rem",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      cursor: "pointer",
      transition: "transform 0.2s, box-shadow 0.2s",
    },
    courseCardHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 12px -1px rgba(0, 0, 0, 0.15)",
    },
    courseTitle: {
      fontSize: "1.25rem",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "0.75rem",
    },
    courseDescription: {
      color: "#6b7280",
      fontSize: "0.875rem",
      marginBottom: "1rem",
      lineHeight: "1.5",
    },
    tagsContainer: {
      display: "flex",
      gap: "0.5rem",
      marginBottom: "1rem",
    },
    levelTag: {
      padding: "0.25rem 0.75rem",
      backgroundColor: "#dbeafe",
      border: "1.5px solid #6C7DF9",
      color: "#1e40af",
      borderRadius: "9999px",
      fontSize: "0.75rem",
      fontWeight: "500",
    },
    statusTagCompleted: {
      padding: "0.25rem 0.75rem",
      backgroundColor: "#dcfce7",
      color: "#166534",
      border: "1.5px solid  #51fa8c",
      borderRadius: "9999px",
      fontSize: "0.75rem",
      fontWeight: "500",
    },
    statusTagInProgress: {
      padding: "0.25rem 0.75rem",
      backgroundColor: "#fefce8",
      color: "#a16207",
      border: "1.5px solid #fde047",
      borderRadius: "9999px",
      fontSize: "0.75rem",
      fontWeight: "500",
    },
    detailContainer: {
      maxWidth: "800px",
      margin: "0 auto",
      backgroundColor: "white",
      padding: "2rem",
      borderRadius: "0.5rem",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    },
    backButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#6b7280",
      color: "white",
      border: "none",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
      marginBottom: "2rem",
      transition: "background-color 0.2s",
    },
    detailTitle: {
      fontSize: "2rem",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "1rem",
    },
    detailDescription: {
      color: "#374151",
      fontSize: "1rem",
      lineHeight: "1.6",
      marginBottom: "2rem",
    },
    detailTagsContainer: {
      display: "flex",
      gap: "1rem",
      marginBottom: "2rem",
    },
    videosSection: {
      marginTop: "2rem",
    },
    videosTitle: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "1rem",
    },
    videoItem: {
      padding: "1rem",
      backgroundColor: "#f9fafb",
      border: "1px solid #e5e7eb",
      borderRadius: "0.375rem",
      marginBottom: "0.75rem",
    },
    videoTitle: {
      fontSize: "1rem",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "0.5rem",
    },
    videoDescription: {
      color: "#6b7280",
      fontSize: "0.875rem",
    },
    videoIndex: {
      display: "inline-block",
      backgroundColor: "#3b82f6",
      color: "white",
      padding: "0.25rem 0.5rem",
      borderRadius: "0.25rem",
      fontSize: "0.75rem",
      fontWeight: "bold",
      marginRight: "0.5rem",
    },
    button: {
      width: "100%",
      padding: "0.75rem 1rem",
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
      borderRadius: "0.375rem",
      fontSize: "1rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s",
      marginTop: "1rem",
    },
    purchaseButton: {
      backgroundColor: "#10b981",
    },
    goToCourseButton: {
      backgroundColor: "#3b82f6",
    },
    loading: {
      textAlign: "center",
      fontSize: "1.125rem",
      color: "#6b7280",
      marginTop: "3rem",
    },
    error: {
      textAlign: "center",
      fontSize: "1rem",
      color: "#dc2626",
      marginTop: "2rem",
      padding: "1rem",
      backgroundColor: "#fef2f2",
      borderRadius: "0.375rem",
      maxWidth: "500px",
      margin: "2rem auto",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading courses...</div>
      </div>
    );
  }

  if (selectedCourse) {
    const isPurchased = isCoursePurchased(selectedCourse._id);

    return (
      <div style={styles.container}>
        <div style={styles.detailContainer}>
          <button
            style={styles.backButton}
            onClick={handleBackToHome}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#4b5563")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6b7280")}
          >
            ← Back to Courses
          </button>

          <h1 style={styles.detailTitle}>{selectedCourse.title}</h1>
          <p style={styles.detailDescription}>{selectedCourse.description}</p>

          <div style={styles.detailTagsContainer}>
            <span style={styles.levelTag}>{selectedCourse.level}</span>
            <span
              style={
                selectedCourse.status === "Completed"
                  ? styles.statusTagCompleted
                  : styles.statusTagInProgress
              }
            >
              {selectedCourse.status}
            </span>
          </div>

          <div style={styles.videosSection}>
            <h2 style={styles.videosTitle}>
              Course Content ({selectedCourse.videos?.length || 0} videos)
            </h2>
            {selectedCourse.videos && selectedCourse.videos.length > 0 ? (
              selectedCourse.videos
                .sort((a, b) => a.index - b.index)
                .map((video) => (
                  <div key={video._id} style={styles.videoItem}>
                    <div style={styles.videoTitle}>
                      <span style={styles.videoIndex}>{video.index}</span>
                      {video.title}
                    </div>
                    <div style={styles.videoDescription}>
                      {video.description}
                    </div>
                  </div>
                ))
            ) : (
              <p style={{ color: "#6b7280", fontStyle: "italic" }}>
                No videos available yet.
              </p>
            )}
          </div>

          <button
            style={{
              ...styles.button,
              ...(isPurchased
                ? styles.goToCourseButton
                : styles.purchaseButton),
            }}
            onClick={() =>
              isPurchased
                ? handleGoToCourse(selectedCourse._id)
                : handlePurchaseCourse(selectedCourse._id)
            }
            onMouseOver={(e) => (e.target.style.opacity = "0.9")}
            onMouseOut={(e) => (e.target.style.opacity = "1")}
          >
            {isPurchased ? "Go to Course" : "Purchase Course"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Available Courses</h1>
        <p style={styles.subtitle}>
          Discover and enroll in our comprehensive course catalog
        </p>
        {/* Temporary Admin Dashboard Button */}
        <p
          style={{
            backgroundColor: "#ff0000",
            width: "fit-content",
            padding: "0.5rem",
            borderRadius: "0.375rem",
            color: "white",
            cursor: "pointer",
            margin: "auto",
          }}
          onClick={() => navigate("/adminhome")}
        >
          Admin Dashboard
        </p>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.coursesGrid}>
        {courses.map((course) => (
          <div
            key={course._id}
            style={styles.courseCard}
            onClick={() => handleCourseClick(course._id)}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, styles.courseCardHover);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
            }}
          >
            <h3 style={styles.courseTitle}>{course.title}</h3>
            <p style={styles.courseDescription}>{course.description}</p>

            <div style={styles.tagsContainer}>
              <span style={styles.levelTag}>{course.level}</span>
              <span
                style={
                  course.status === "Completed"
                    ? styles.statusTagCompleted
                    : styles.statusTagInProgress
                }
              >
                {course.status}
              </span>
            </div>
          </div>
        ))}
      </div>

      {courses.length === 0 && !loading && (
        <div style={styles.loading}>No courses available at the moment.</div>
      )}
    </div>
  );
};

export default Home;
